import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SettingService } from '../setting.service';
import { DefaultFees, PortalConfig } from '../setting.type';
import { Header } from '../../shared/header/header';
import { AuthService } from '../../auth/auth.service';

/**
 * Default fees and supplier-portal configuration.
 *
 * Two things this screen is careful about:
 *  - A fee default that has never been set is shown as UNSET, not as 0. Until it
 *    is set, creating a stock or a manual contract fails loudly.
 *  - Changing a default never rewrites existing records: each stock and contract
 *    snapshots its rate at creation, and each transfer run snapshots again when
 *    it is claimed.
 */
@Component({
  selector: 'app-supplier-settings',
  imports: [
    CommonModule, FormsModule, MatCardModule, MatButtonModule, MatIconModule,
    MatFormFieldModule, MatInputModule, MatSlideToggleModule,
    MatProgressSpinnerModule, Header,
  ],
  templateUrl: './supplier-settings.html',
  styleUrl: './supplier-settings.scss',
})
export class SupplierSettings implements OnInit {
  readonly canEdit = inject(AuthService).canWrite('suppliers_payments');
  fees: DefaultFees | null = null;
  portal: PortalConfig | null = null;
  loading = true;
  savingFees = false;
  savingPortal = false;

  stockFee: number | null = null;
  contractFee: number | null = null;

  constructor(private service: SettingService, private snackBar: MatSnackBar) { }

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading = true;
    this.service.getDefaultFees().subscribe({
      next: f => {
        this.fees = f;
        // Leave the input blank when unset, so an admin cannot mistake an
        // unconfigured default for a deliberate zero.
        this.stockFee = f.default_stock_fee_set ? f.default_stock_fee : null;
        this.contractFee = f.default_manual_contract_fee_set ? f.default_manual_contract_fee : null;
        this.loading = false;
      },
      error: () => { this.loading = false; },
    });
    this.service.getPortalConfig().subscribe({
      next: p => this.portal = p,
      error: () => { },
    });
  }

  saveFees(): void {
    this.savingFees = true;
    this.service.updateDefaultFees({
      default_stock_fee: this.stockFee ?? undefined,
      default_manual_contract_fee: this.contractFee ?? undefined,
    }).subscribe({
      next: f => {
        this.fees = f;
        this.savingFees = false;
        this.snackBar.open(
          'Defaults saved. Existing stocks and contracts keep the rate they were created with.',
          'Close', { duration: 6000 });
      },
      error: err => {
        this.savingFees = false;
        this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 5000 });
      },
    });
  }

  savePortal(): void {
    if (!this.portal) return;
    this.savingPortal = true;
    this.service.updatePortalConfig({
      open_webapp_price: this.portal.open_webapp_price,
      closed_webapp_price: this.portal.closed_webapp_price,
      buying_enabled: this.portal.buying_enabled,
      telegram_support_url: this.portal.telegram_support_url,
      tutorials_url: this.portal.tutorials_url,
      sales_site_url: this.portal.sales_site_url,
      notice_html: this.portal.notice_html,
      payment_notice: this.portal.payment_notice,
    }).subscribe({
      next: p => {
        this.portal = p;
        this.savingPortal = false;
        this.snackBar.open('Portal configuration saved.', 'Close', { duration: 4000 });
      },
      error: err => {
        this.savingPortal = false;
        this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 5000 });
      },
    });
  }
}
